import { Component, computed, signal, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { EnumValues, GlobalEntityDefinition, UserLite, Note, EnumObjectExtended, BreadcrumbService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { Observable, finalize, map } from 'rxjs';
import _ from 'lodash';
import {
  AvailableLotItemDto,
  LotItemTransferDto,
  LotItemTransferStatus,
  LotItemTransferType,
  LotItemTransferStatusCommand,
  BatchAddLotItemKeyCommand,
  LotItemTransferStatusHistoryEntry,
  RelocationWarehouse,
} from '../../../models/inventory/lot-item-transfer';
import { RELOCATION_SITE_PREFIX } from '../../../models/inventory/lot-item-transfer.constants';
import { RollSearchCommand, RollCategory, RollQuality } from '../../../models/production/roll-search';

import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { LotItemTransferProxyService } from '../../../proxies/inventory/lot-item-transfer.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { RollProxyService } from '../../../proxies/production/roll.proxy';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { ProductionCardsProxyService } from '../../../proxies/production/production-card.proxy.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { ImportantLocations, LocationNode } from '../../../models/location';
import { ProductionCard } from '../../../models/production/production-card';
import { WorkOrder, WorkOrderSearchCommand, WorkOrderType } from '../../../models/order/work-order';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '../../../models/production/roll';
import { LotItemSearchCommand, LotItemSearchResultItem, LotItemType } from '../../../models/inventory/lot-item-search';

@Component({
  selector: 'app-lot-item-transfer-details',
  templateUrl: './lot-item-transfer-details.component.html',
  styleUrl: './lot-item-transfer-details.component.scss',
})
export class LotItemTransferDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly userAuthService = inject(UserAuthenticationService);
  private readonly lotItemTransferProxyService = inject(LotItemTransferProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly workOrderProxyService = inject(WorkOrderProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  relocationWarehouse = signal<RelocationWarehouse>(RelocationWarehouse.FinishedGoods);

  readonly LotItemTransferType = LotItemTransferType;
  readonly LotItemTransferStatus = LotItemTransferStatus;

  get selectedTransferType() {
    return this.originalTransfer()?.transferType ?? null;
  }

  originalTransfer = signal<LotItemTransferDto | null>(null);

  loading = signal<number>(0);

  status: FormControl<number | null> = new FormControl<number | null>(LotItemTransferStatus.Draft);
  statuses: EnumValues;
  statusEnumObject?: EnumObjectExtended;
  transferTypes: EnumValues;

  userLite: UserLite | null = null;

  searchResults = signal<Roll[]>([]);

  addedRolls = signal<Roll[]>([]);

  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);

  rollSearchTerm = signal<string>('');

  selectedProductionCard = signal<ProductionCard | null>(null);

  selectedWorkOrder = signal<WorkOrder | null>(null);

  sourceLocationTree = signal<TreeNode[]>([]);
  targetLocationTree = signal<TreeNode[]>([]);
  selectedSourceLocation = signal<TreeNode | null>(null);
  selectedTargetLocation = signal<TreeNode | null>(null);

  // Receiving fires a confirm dialog from valueChanges; without this flag a second emission opens a duplicate.
  private isConfirmationPending = false;

  private debouncedSearchRolls = _.debounce(() => this.searchRolls(1), 3000);

  lotItemSearchResults = signal<LotItemSearchResultItem[]>([]);
  addedLotItems = signal<LotItemSearchResultItem[]>([]);
  lotItemSearchTerm = signal<string>('');
  lotItemCurrentPage = signal<number>(1);
  lotItemTotalRecords = signal<number>(0);

  isRawMaterialToFinishedGoods = computed(() => this.selectedTransferType === LotItemTransferType.RawMaterialToFinishedGoods);
  isFinishedGoodsToRawMaterial = computed(() => this.selectedTransferType === LotItemTransferType.FinishedGoodsToRawMaterial);
  isChemicalBatchToFinishedGoods = computed(() => this.selectedTransferType === LotItemTransferType.ChemicalBatchToFinishedGoods);
  isRawMaterialRelocation = computed(() => this.isRelocationTransfer() && this.relocationWarehouse() === RelocationWarehouse.RawMaterial);
  isGreigeSample = computed(() => this.selectedTransferType === LotItemTransferType.GreigeSample);
  isFinishedSample = computed(() => this.selectedTransferType === LotItemTransferType.FinishedSample);
  isSampleTransfer = computed(() => this.isGreigeSample() || this.isFinishedSample());
  hasNotes = computed(() => (this.originalTransfer()?.notes?.length ?? 0) > 0);
  isLotItemBasedTransfer = computed(
    // TransferToFinishedGoods (type 5) is greige and roll-based: it must render the roll panel
    // and the GreigeOnly work-order step, so it is intentionally excluded from lot-item gating.
    () =>
      this.isRawMaterialToFinishedGoods() ||
      this.isFinishedGoodsToRawMaterial() ||
      this.isChemicalBatchToFinishedGoods() ||
      this.isRawMaterialRelocation(),
  );

  private debouncedSearchLotItems = _.debounce(() => this.searchLotItems(1), 1500);

  rmRelocationResults = signal<AvailableLotItemDto[]>([]);
  rmRelocationCurrentPage = signal<number>(1);
  rmRelocationTotalRecords = signal<number>(0);
  rmRelocationLotNumberFilter = signal<string>('');
  rmRelocationItemCodeFilter = signal<string>('');
  rmRelocationManufacturerFilter = signal<string>('');
  rmRelocationProviderFilter = signal<string>('');
  rmRelocationMfrLotFilter = signal<string>('');
  private debouncedSearchRmRelocationItems = _.debounce(() => this.searchAvailableLotItemsForRelocation(1), 1000);

  lotItemItemCodeFilter = signal<string>('');
  lotItemManufacturerLotFilter = signal<string>('');
  lotItemManufacturerFilter = signal<Business | null>(null);
  lotItemManufacturerSuggestions = signal<Business[]>([]);
  // Every lot item created from a batch carries Pride Chemicals as manufacturer, so the filter would be a no-op there.
  readonly showLotItemManufacturerFilter = computed(() => !this.isChemicalBatchToFinishedGoods());
  private debouncedSearchLotItemsFiltered = _.debounce(() => this.searchLotItems(1), 1000);

  // Per-itemCode UOM resolved from the ItemMaster. Item masters can change UOMs between lot
  // items, so we resolve and display the UOM per row instead of in the column header.
  private uomByItemCode = signal<Map<string, number>>(new Map());
  private unitOfMeasureValues: Record<string, string> = {};

  constructor() {
    this.statusEnumObject = this.enumService.getEnum('LotItemTransferStatus', 'inventory');
    this.statuses = this.statusEnumObject?.values ?? {};
    this.transferTypes = this.enumService.getEnum('LotItemTransferType', 'inventory').values;
    this.unitOfMeasureValues = this.enumService.getEnum('UnitOfMeasure', 'item').values as Record<string, string>;
  }

  sourceLocationCode = computed(() => {
    const node = this.selectedSourceLocation();
    return node?.data?.locationCode || null;
  });

  targetLocationCode = computed(() => {
    const node = this.selectedTargetLocation();
    return node?.data?.locationCode || null;
  });

  isRelocationTransfer = computed(() => this.originalTransfer()?.transferType === LotItemTransferType.Relocation);

  isFinishedGoodsTransfer = computed(() => this.originalTransfer()?.transferType === LotItemTransferType.FinishedGoodsTransfer);

  // Greige rolls have no FinalWeight yet, so their weight comes from the last station timestamp.
  readonly useStationWeight = computed(
    () =>
      this.selectedTransferType === LotItemTransferType.TransferToFinishedGoods ||
      this.selectedTransferType === LotItemTransferType.GreigeSample,
  );

  readonly requiresWorkOrderForSearch = computed(() => this.selectedTransferType === LotItemTransferType.TransferToFinishedGoods);

  readonly showProductionCardFilter = computed(() => this.selectedTransferType !== LotItemTransferType.TransferToFinishedGoods);

  readonly = computed(() => {
    const transfer = this.originalTransfer();
    return transfer !== null && transfer.status !== LotItemTransferStatus.Draft;
  });

  addedItemNumbers = computed(() => {
    const transfer = this.originalTransfer();
    return new Set(transfer?.items?.map((i) => i.number) || []);
  });

  canAddAll = computed(() => {
    if (this.readonly()) return false;
    return this.selectedProductionCard() !== null;
  });

  availableRolls = computed(() => {
    const added = this.addedItemNumbers();
    return this.searchResults().filter((roll) => !added.has(roll.number));
  });

  totalWeightLbs = computed(() => {
    const useStation = this.useStationWeight();
    return this.addedRolls().reduce((sum, roll) => {
      const weight = useStation ? roll.lastRecordedWeightLbs || 0 : roll.finalWeightLbs || 0;
      return sum + weight;
    }, 0);
  });

  totalWeightKg = computed(() => {
    return this.lbsToKg(this.totalWeightLbs());
  });

  public get roleStatusOptions(): EnumValues {
    if (!this.statuses || typeof this.statuses !== 'object') return {};

    const currentStatus = this.originalTransfer()?.status ?? this.status.value ?? LotItemTransferStatus.Draft;
    const filtered: EnumValues = {};

    if (currentStatus === LotItemTransferStatus.Received || currentStatus === LotItemTransferStatus.Voided) {
      filtered[currentStatus] = this.statuses[currentStatus];
      return filtered;
    }

    let allowedStatusesByRole: LotItemTransferStatus[] = [];

    if (
      AuthService.hasAnyRole([
        UserGroup.LotItemTransferEditor,
        UserGroup.LotItemTransferAdmin,
        UserGroup.FinishedGoodsRelocationAdmin,
        UserGroup.RawMaterialRelocationAdmin,
      ])
    ) {
      allowedStatusesByRole.push(
        LotItemTransferStatus.Draft,
        LotItemTransferStatus.ReadyForTransfer,
        LotItemTransferStatus.InRoute,
        LotItemTransferStatus.Voided,
      );
    }

    if (
      AuthService.hasAnyRole([
        UserGroup.LotItemTransferReceiver,
        UserGroup.LotItemTransferAdmin,
        UserGroup.FinishedGoodsRelocationAdmin,
        UserGroup.RawMaterialRelocationAdmin,
      ])
    ) {
      allowedStatusesByRole.push(LotItemTransferStatus.Received);
    }

    const transferType = this.selectedTransferType;
    if (transferType === LotItemTransferType.GreigeSample && AuthService.hasRole(UserGroup.GreigeSampleSender)) {
      allowedStatusesByRole.push(
        LotItemTransferStatus.Draft,
        LotItemTransferStatus.ReadyForTransfer,
        LotItemTransferStatus.InRoute,
        LotItemTransferStatus.Voided,
      );
    }
    if (transferType === LotItemTransferType.GreigeSample && AuthService.hasRole(UserGroup.GreigeSampleReceiver)) {
      allowedStatusesByRole.push(LotItemTransferStatus.Received);
    }
    if (transferType === LotItemTransferType.FinishedSample && AuthService.hasRole(UserGroup.FinishedSampleSender)) {
      allowedStatusesByRole.push(
        LotItemTransferStatus.Draft,
        LotItemTransferStatus.ReadyForTransfer,
        LotItemTransferStatus.InRoute,
        LotItemTransferStatus.Voided,
      );
    }
    if (transferType === LotItemTransferType.FinishedSample && AuthService.hasRole(UserGroup.FinishedSampleReceiver)) {
      allowedStatusesByRole.push(LotItemTransferStatus.Received);
    }

    allowedStatusesByRole = [...new Set(allowedStatusesByRole)].sort();

    let allowedStatusesByTransition: LotItemTransferStatus[] = [];

    switch (currentStatus) {
      case LotItemTransferStatus.Draft:
        allowedStatusesByTransition = [LotItemTransferStatus.ReadyForTransfer, LotItemTransferStatus.Voided];
        break;
      case LotItemTransferStatus.ReadyForTransfer:
        allowedStatusesByTransition = [LotItemTransferStatus.InRoute, LotItemTransferStatus.Voided];
        break;
      case LotItemTransferStatus.InRoute:
        allowedStatusesByTransition = [LotItemTransferStatus.Received];
        break;
      case LotItemTransferStatus.Received:
      case LotItemTransferStatus.Voided:
        allowedStatusesByTransition = [];
        break;
      default:
        allowedStatusesByTransition = allowedStatusesByRole;
    }

    let finalAllowedStatuses = allowedStatusesByRole.filter((status) => allowedStatusesByTransition.includes(status));

    // Sample transfers require notes before advancing past Draft
    if (this.isSampleTransfer() && !this.hasNotes()) {
      finalAllowedStatuses = finalAllowedStatuses.filter((s) => s === LotItemTransferStatus.Draft || s === LotItemTransferStatus.Voided);
    }

    // Always include current status so the dropdown renders with a valid selected value
    if (this.statuses[currentStatus]) {
      filtered[currentStatus] = this.statuses[currentStatus];
    }

    for (const key of finalAllowedStatuses) {
      if (this.statuses[key]) {
        filtered[key] = this.statuses[key];
      }
    }

    return filtered;
  }

  get isLoading(): boolean {
    return this.loading() > 0;
  }

  private increaseLoading(): void {
    this.loading.update((n) => n + 1);
  }

  private decreaseLoading(): void {
    this.loading.update((n) => Math.max(0, n - 1));
  }

  private translate(key: string, fallback: string, params?: Record<string, string>): string {
    const translation = this.translateService.instant(key, params);
    if (translation !== key) {
      return translation;
    }
    if (params) {
      let result = fallback;
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      }
      return result;
    }
    return fallback;
  }

  getStatusHistoryStrict(statusHistory: unknown): LotItemTransferStatusHistoryEntry {
    return statusHistory as LotItemTransferStatusHistoryEntry;
  }

  getLotItemEntryStrict(lotItemEntry: unknown): LotItemSearchResultItem {
    return lotItemEntry as LotItemSearchResultItem;
  }

  canEdit(): boolean {
    if (AuthService.hasAnyRole([UserGroup.LotItemTransferEditor, UserGroup.LotItemTransferAdmin])) {
      return true;
    }
    if (this.isRelocationTransfer() && !this.isRawMaterialRelocation() && AuthService.hasRole(UserGroup.FinishedGoodsRelocationAdmin)) {
      return true;
    }
    if (this.isRawMaterialRelocation() && AuthService.hasRole(UserGroup.RawMaterialRelocationAdmin)) {
      return true;
    }
    if (this.isGreigeSample() && AuthService.hasRole(UserGroup.GreigeSampleSender)) {
      return true;
    }
    if (this.isFinishedSample() && AuthService.hasRole(UserGroup.FinishedSampleSender)) {
      return true;
    }
    return false;
  }

  ngOnInit(): void {
    this.userLite = this.userAuthService.userLite;

    this.route.queryParams.subscribe((params) => {
      const id = params['id'];

      if (_.isNil(id)) {
        this.router.navigate(['/lot-item-transfers']);
      } else {
        this.loadTransfer(id);
      }
    });

    this.status.valueChanges.subscribe((value) => {
      if (value !== null && this.originalTransfer()) {
        if (value === LotItemTransferStatus.Received) {
          if (this.isConfirmationPending) {
            return;
          }
          this.isConfirmationPending = true;

          this.confirmationService.confirm({
            message: this.translate('Confirm_Receive_Message', 'Are you sure you want to mark this transfer as received?'),
            header: this.translate('Confirm_Receive_Header', 'Confirm Receive'),
            icon: 'pi pi-check-circle',
            acceptLabel: this.translate('Confirm', 'Confirm'),
            rejectLabel: this.translate('Cancel', 'Cancel'),
            key: 'main',
            closable: false,
            accept: () => {
              this.isConfirmationPending = false;
              this.updateStatus(value);
            },
            reject: () => {
              this.isConfirmationPending = false;
              const transfer = this.originalTransfer();
              if (transfer) {
                this.status.setValue(transfer.status, { emitEvent: false });
              }
            },
          });
        } else {
          this.updateStatus(value);
        }
      }
    });
  }

  private loadLocationTrees(onLoaded?: (sourceTree: TreeNode[], targetTree: TreeNode[]) => void): void {
    const prefix = RELOCATION_SITE_PREFIX + this.relocationWarehouse();
    this.locationProxyService.getAllChildrenLocations(prefix).subscribe({
      next: (response) => {
        if (response.data) {
          // The API may return sibling warehouses; keep only this warehouse's locations
          const filtered = response.data.filter((loc) => loc.locationCode.startsWith(prefix));
          const sourceTree = this.convertLocationsToTreeNodes(filtered, false);
          this.sourceLocationTree.set(sourceTree);

          const targetTree = this.convertLocationsToTreeNodes(filtered, true);
          this.targetLocationTree.set(targetTree);

          onLoaded?.(sourceTree, targetTree);
        }
      },
    });
  }

  private loadTransfer(id: string): void {
    this.increaseLoading();
    this.lotItemTransferProxyService
      .getById(id)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.breadcrumbService.setCurrent(response.data.number);
            this.status.setValue(response.data.status, { emitEvent: false });

            if (response.data.transferType === LotItemTransferType.Relocation) {
              const isRawMaterial = response.data.fromLocationCode?.startsWith(ImportantLocations.RAW_MATERIAL);
              if (isRawMaterial) {
                this.relocationWarehouse.set(RelocationWarehouse.RawMaterial);
              }
              const transfer = response.data;
              this.loadLocationTrees((sourceTree, targetTree) => {
                const sourceNode = this.findNodeByLocationCode(sourceTree, transfer.fromLocationCode);
                if (sourceNode) this.selectedSourceLocation.set(sourceNode);
                const targetNode = this.findNodeByLocationCode(targetTree, transfer.toLocationCode);
                if (targetNode) this.selectedTargetLocation.set(targetNode);
              });
              if (isRawMaterial) {
                this.loadLotItemDetailsForAddedItems();
                if (response.data.status === LotItemTransferStatus.Draft) {
                  this.searchAvailableLotItemsForRelocation();
                }
              } else {
                this.loadRollDetailsForAddedItems();
                if (response.data.status === LotItemTransferStatus.Draft) {
                  this.searchRolls();
                }
              }
            } else if (
              response.data.transferType === LotItemTransferType.RawMaterialToFinishedGoods ||
              response.data.transferType === LotItemTransferType.FinishedGoodsToRawMaterial ||
              response.data.transferType === LotItemTransferType.ChemicalBatchToFinishedGoods
            ) {
              this.loadLotItemDetailsForAddedItems();
              if (response.data.status === LotItemTransferStatus.Draft) {
                this.searchLotItems();
              }
            } else {
              this.loadRollDetailsForAddedItems();
              if (response.data.status === LotItemTransferStatus.Draft) {
                this.searchRolls();
              }
            }
          } else {
            this.router.navigate(['/lot-item-transfers']);
          }
        },
        error: () => {
          this.router.navigate(['/lot-item-transfers']);
        },
      });
  }

  private findNodeByLocationCode(nodes: TreeNode[], locationCode: string): TreeNode | null {
    for (const node of nodes) {
      if (node.data?.locationCode === locationCode) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByLocationCode(node.children, locationCode);
        if (found) return found;
      }
    }
    return null;
  }

  private autoSetSourceLocation(locationCode: string, callback?: () => void): void {
    const transfer = this.originalTransfer();
    if (!transfer) {
      callback?.();
      return;
    }

    if (transfer.fromLocationCode === locationCode) {
      callback?.();
      return;
    }

    const node = this.findNodeByLocationCode(this.sourceLocationTree(), locationCode);
    if (node) {
      this.selectedSourceLocation.set(node);
    }

    const targetCode = this.targetLocationCode() || transfer.toLocationCode;

    this.increaseLoading();
    this.lotItemTransferProxyService
      .updateLocations(transfer.id, locationCode, targetCode)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
          }
          callback?.();
        },
        error: () => {
          callback?.();
        },
      });
  }

  private loadRollDetailsForAddedItems(): void {
    const addedRollNumbers = this.originalTransfer()?.items.map((i) => i.number) || [];
    if (addedRollNumbers.length === 0) return;

    const command: RollSearchCommand = {
      rollNumbers: addedRollNumbers,
    };

    const addedNumbersSet = new Set(addedRollNumbers);

    this.increaseLoading();
    this.rollProxyService
      .searchRoll(command, 1, 1000)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const exactMatches = response.data.items.filter((roll) => addedNumbersSet.has(roll.number));
            this.addedRolls.set(exactMatches);
          }
        },
      });
  }

  private extractWarehousePrefix(locationCode: string): string {
    const parts = locationCode.split('.');
    if (parts.length >= 2) {
      return `${parts[0]}.${parts[1]}`;
    }
    return locationCode;
  }

  searchRolls(page = 1): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    // TransferToFinishedGoods requires a work order filter before searching
    if (this.requiresWorkOrderForSearch() && !this.selectedWorkOrder()) {
      this.searchResults.set([]);
      this.totalRecords.set(0);
      return;
    }

    const command: RollSearchCommand = {
      locationCodes: [transfer.fromLocationCode],
    };

    const rollTerm = this.rollSearchTerm().trim();
    if (rollTerm) {
      command.rollNumbers = [rollTerm];
    }

    const productionCard = this.selectedProductionCard();
    if (productionCard) {
      command.productionCardReferences = [productionCard.id!];
    }

    const workOrder = this.selectedWorkOrder();
    if (workOrder) {
      command.workOrderReferences = [workOrder.id];
    }

    const excludeNumbers = this.originalTransfer()?.items.map((i) => i.number) || [];
    if (excludeNumbers.length > 0) {
      command.excludeRollNumbers = excludeNumbers;
    }

    this.increaseLoading();
    this.rollProxyService
      .searchRoll(command, page, this.pageSize())
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.searchResults.set(response.data.items);
            this.totalRecords.set(response.data.totalCount);
            this.currentPage.set(page);
          }
        },
        error: () => {
          this.searchResults.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize();
    const page = Math.floor(first / rows) + 1;
    this.pageSize.set(rows);
    this.searchRolls(page);
  }

  onRollSearchTermChange(term: string): void {
    this.rollSearchTerm.set(term);
    this.debouncedSearchRolls();
  }

  onSourceLocationSelect(event: TreeNodeSelectEvent): void {
    this.selectedSourceLocation.set(event.node);
    this.selectedTargetLocation.set(null);
    this.updateLocationsIfNeeded();
  }

  onTargetLocationSelect(event: TreeNodeSelectEvent): void {
    this.selectedTargetLocation.set(event.node);
    this.updateLocationsIfNeeded();
  }

  private updateLocationsIfNeeded(): void {
    const transfer = this.originalTransfer();
    if (!transfer || transfer.status !== LotItemTransferStatus.Draft) return;

    const sourceCode = this.sourceLocationCode();
    const targetCode = this.targetLocationCode();

    if (!sourceCode || !targetCode) return;
    if (sourceCode === targetCode) {
      GlobalMessageUtil.error(
        this.translate('Error', 'Error'),
        this.translate('Source_Target_Same', 'Source and target locations cannot be the same'),
      );
      this.selectedTargetLocation.set(null);
      return;
    }

    const sourceWarehouse = this.extractWarehousePrefix(sourceCode);
    const targetWarehouse = this.extractWarehousePrefix(targetCode);
    if (sourceWarehouse !== targetWarehouse) {
      GlobalMessageUtil.error(
        this.translate('Error', 'Error'),
        this.translate('Same_Warehouse_Required', 'Relocation requires source and target to be in the same warehouse'),
      );
      return;
    }

    this.increaseLoading();
    this.lotItemTransferProxyService
      .updateLocations(transfer.id, sourceCode, targetCode)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Locations_Updated', 'Locations updated successfully'),
            );
            if (this.isRawMaterialRelocation()) {
              this.searchAvailableLotItemsForRelocation();
            } else {
              this.searchRolls();
            }
          }
        },
      });
  }

  public fetchProductionCards = (query: string): Observable<ProductionCard[]> =>
    this.productionCardProxyService.search({ productionCardNumber: query }).pipe(map((response) => response.data ?? []));

  onProductionCardSelect(event: AutoCompleteSelectEvent): void {
    const pc = event.value as ProductionCard;
    this.selectedProductionCard.set(pc);

    // For relocation with no items: find where the PC's rolls are and auto-set source
    if (this.isRelocationTransfer() && this.originalTransfer()?.items.length === 0) {
      const command: RollSearchCommand = {
        productionCardReferences: [pc.id!],
      };
      this.increaseLoading();
      this.rollProxyService
        .searchRoll(command, 1, 1)
        .pipe(finalize(() => this.decreaseLoading()))
        .subscribe({
          next: (response) => {
            if (response.success && response.data && response.data.items.length > 0) {
              const locationCode = response.data.items[0].currentLocationCode;
              if (locationCode) {
                this.autoSetSourceLocation(locationCode, () => this.searchRolls());
                return;
              }
            }
            this.searchRolls();
          },
          error: () => {
            this.searchRolls();
          },
        });
    } else {
      this.searchRolls();
    }
  }

  onProductionCardClear(): void {
    this.selectedProductionCard.set(null);
    this.searchRolls();
  }

  public fetchWorkOrders = (query: string): Observable<WorkOrder[]> => {
    const orderType =
      this.originalTransfer()?.transferType === LotItemTransferType.TransferToFinishedGoods ? WorkOrderType.GreigeOnly : undefined;

    const command: WorkOrderSearchCommand = { orderNumber: query, orderType };

    return this.workOrderProxyService.search(command, 1, 20).pipe(map((response) => response?.data?.items || []));
  };

  onWorkOrderSelect(event: AutoCompleteSelectEvent): void {
    this.selectedWorkOrder.set(event.value as WorkOrder);
    this.searchRolls();
  }

  onWorkOrderClear(): void {
    this.selectedWorkOrder.set(null);
    this.searchRolls();
  }

  clearAllFilters(): void {
    this.rollSearchTerm.set('');
    this.selectedProductionCard.set(null);
    this.selectedWorkOrder.set(null);
    this.searchRolls();
  }

  addItem(roll: Roll): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    if (this.isRelocationTransfer() && !this.targetLocationCode()) {
      GlobalMessageUtil.error(
        this.translate('Error', 'Error'),
        this.translate('Target_Location_Required', 'Please select a target location before adding items'),
      );
      return;
    }

    // For relocation: auto-set source location from first roll added
    if (this.isRelocationTransfer() && transfer.items.length === 0 && roll.currentLocationCode) {
      this.autoSetSourceLocation(roll.currentLocationCode, () => this.performAddItem(roll));
      return;
    }

    this.performAddItem(roll);
  }

  private performAddItem(roll: Roll): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    const command: BatchAddLotItemKeyCommand = {
      items: [
        {
          number: roll.number,
          entityDefinitionCode: GlobalEntityDefinition.RollItem,
        },
      ],
    };

    this.increaseLoading();
    this.lotItemTransferProxyService
      .batchAddItems(transfer.id, command)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.addedRolls.update((current) => [...current, roll]);
            this.totalRecords.update((n) => Math.max(0, n - 1));
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Roll_Added_Success', 'Roll {{number}} added', { number: roll.number }),
            );
          }
        },
      });
  }

  addAllAvailable(): void {
    const transfer = this.originalTransfer();
    const productionCard = this.selectedProductionCard();
    if (!transfer || !productionCard || transfer.status !== LotItemTransferStatus.Draft) return;

    const command: RollSearchCommand = {
      locationCodes: [transfer.fromLocationCode],
      productionCardReferences: [productionCard.id!],
    };

    const rollTerm = this.rollSearchTerm().trim();
    if (rollTerm) {
      command.rollNumbers = [rollTerm];
    }

    this.increaseLoading();
    this.rollProxyService
      .searchRoll(command, 1, 1000)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data || response.data.items.length === 0) {
            GlobalMessageUtil.info(
              this.translate('Info', 'Info'),
              this.translate('No_Rolls_To_Add', 'No available rolls found at the source location.'),
            );
            return;
          }

          const added = this.addedItemNumbers();
          const rollsToAdd = response.data.items.filter((roll) => !added.has(roll.number));

          if (rollsToAdd.length === 0) {
            GlobalMessageUtil.info(
              this.translate('Info', 'Info'),
              this.translate('All_Rolls_Already_Added', 'All available rolls have already been added.'),
            );
            return;
          }

          const batchCommand: BatchAddLotItemKeyCommand = {
            items: rollsToAdd.map((roll) => ({
              number: roll.number,
              entityDefinitionCode: GlobalEntityDefinition.RollItem,
            })),
          };

          this.increaseLoading();
          this.lotItemTransferProxyService
            .batchAddItems(transfer.id, batchCommand)
            .pipe(finalize(() => this.decreaseLoading()))
            .subscribe({
              next: (batchResponse) => {
                if (batchResponse.success && batchResponse.data) {
                  this.originalTransfer.set(batchResponse.data);
                  this.loadRollDetailsForAddedItems();
                  this.searchRolls(this.currentPage());
                  GlobalMessageUtil.success(
                    this.translate('Success', 'Success'),
                    this.translate('Rolls_Added_Success', '{{count}} rolls added', {
                      count: rollsToAdd.length.toString(),
                    }),
                  );
                }
              },
            });
        },
      });
  }

  removeItem(item: Roll): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    const itemInTransfer = transfer.items.find((i) => i.number === item.number);

    if (!itemInTransfer) return;

    this.increaseLoading();
    this.lotItemTransferProxyService
      .removeItem(transfer.id, item.number, itemInTransfer.entityDefinitionCode)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.addedRolls.update((current) => current.filter((r) => r.number !== item.number));
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Item_Removed_Success', 'Item {{number}} removed', { number: item.number }),
            );
            // Reload available rolls to show the removed item again
            this.searchRolls(this.currentPage());
          }
        },
      });
  }

  // ==================== Lot Item Methods ====================

  private loadLotItemDetailsForAddedItems(): void {
    const numbers = this.originalTransfer()?.items.map((i) => i.number) || [];
    if (numbers.length === 0) return;

    this.increaseLoading();
    this.lotItemProxyService
      .search({
        lotItemNumbers: numbers,
      })
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.addedLotItems.set(response.data.items);
            this.hydrateUomCacheForItems(response.data.items);
          }
        },
      });
  }

  searchLotItems(page = 1): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    const term = this.lotItemSearchTerm().trim();
    const isChemical = this.isChemicalBatchToFinishedGoods();
    const batchReference = isChemical ? transfer.initiator?.reference : undefined;

    // Source warehouse is derived from the transfer's own FromLocationCode so the search is
    // site-agnostic (e.g. NT.WH-RM-1 for Northern Textiles, PC.WH-RM-1 for Pride Chemicals).
    const [siteCode, warehouseCode] = transfer.fromLocationCode.split('.');
    const locationPath = { siteCode, warehouseCode };

    const itemCodeFilter = this.lotItemItemCodeFilter().trim() || undefined;
    const mfrLotFilter = this.lotItemManufacturerLotFilter().trim() || undefined;
    const manufacturerId = this.lotItemManufacturerFilter()?.id;

    const command: LotItemSearchCommand = {
      locationPaths: [locationPath],
      lotItemTypes: isChemical ? [LotItemType.Chemical] : undefined,
      batchReferences: batchReference ? [batchReference] : undefined,
      lotItemNumbers: term ? [term] : undefined,
      itemCodes: itemCodeFilter ? [itemCodeFilter] : undefined,
      manufacturerReferences: manufacturerId ? [manufacturerId] : undefined,
      manufacturerLotNumbers: mfrLotFilter ? [mfrLotFilter] : undefined,
      excludeZeroBalances: true,
    };

    const addedNumbers = new Set(transfer.items.map((i) => i.number));

    this.increaseLoading();
    this.lotItemProxyService
      .search(command, page, this.pageSize())
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const filtered = response.data.items.filter((li) => !addedNumbers.has(li.number!));
            this.lotItemSearchResults.set(filtered);
            // Total of items NOT yet added. The backend can't filter by addedNumbers (those are
            // local to the transfer), so subtract on the client.
            this.lotItemTotalRecords.set(Math.max(0, response.data.totalCount - addedNumbers.size));
            this.lotItemCurrentPage.set(page);
            this.hydrateUomCacheForItems(filtered);
          }
        },
        error: () => {
          this.lotItemSearchResults.set([]);
          this.lotItemTotalRecords.set(0);
        },
      });
  }

  onLotItemPageChange(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize();
    const page = Math.floor(first / rows) + 1;
    this.pageSize.set(rows);
    this.searchLotItems(page);
  }

  onLotItemSearchTermChange(term: string): void {
    this.lotItemSearchTerm.set(term);
    this.debouncedSearchLotItems();
  }

  addLotItem(lotItem: LotItemSearchResultItem): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    const command: BatchAddLotItemKeyCommand = {
      items: [{ number: lotItem.number!, entityDefinitionCode: GlobalEntityDefinition.LotItem }],
    };

    this.increaseLoading();
    this.lotItemTransferProxyService
      .batchAddItems(transfer.id, command)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.addedLotItems.update((current) => [...current, lotItem]);
            this.lotItemSearchResults.update((current) => current.filter((li) => li.number !== lotItem.number));
            this.lotItemTotalRecords.update((n) => Math.max(0, n - 1));
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Lot_Item_Added_Success', 'Lot item {{number}} added', { number: lotItem.number! }),
            );
          }
        },
      });
  }

  removeLotItem(lotItem: LotItemSearchResultItem): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    this.increaseLoading();
    this.lotItemTransferProxyService
      .removeItem(transfer.id, lotItem.number!, GlobalEntityDefinition.LotItem)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.addedLotItems.update((current) => current.filter((li) => li.number !== lotItem.number!));
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Lot_Item_Removed_Success', 'Lot item {{number}} removed', { number: lotItem.number! }),
            );
            this.searchLotItems(this.lotItemCurrentPage());
          }
        },
      });
  }

  getLotItemQuantity(lotItem: LotItemSearchResultItem): number {
    return lotItem.currentQuantity ?? 0;
  }

  getLotItemUomLabel(lotItem: LotItemSearchResultItem): string {
    const uom = this.uomByItemCode().get(lotItem.itemCode);
    if (uom == null) return '';
    return this.unitOfMeasureValues[String(uom)] ?? '';
  }

  private hydrateUomCacheForItems(items: LotItemSearchResultItem[]): void {
    const cache = this.uomByItemCode();
    const missing = Array.from(new Set(items.map((i) => i.itemCode).filter((code): code is string => !!code && !cache.has(code))));
    if (missing.length === 0) return;

    this.itemMasterProxyService.getByItemCodes(missing).subscribe({
      next: (response) => {
        if (!response?.data) return;
        const next = new Map(this.uomByItemCode());
        for (const im of response.data) {
          next.set(im.itemCode, im.unitOfMeasure);
        }
        this.uomByItemCode.set(next);
      },
    });
  }

  clearLotItemFilters(): void {
    this.lotItemSearchTerm.set('');
    this.lotItemItemCodeFilter.set('');
    this.lotItemManufacturerLotFilter.set('');
    this.lotItemManufacturerFilter.set(null);
    if (this.isRawMaterialRelocation()) {
      this.searchAvailableLotItemsForRelocation(1);
    } else {
      this.searchLotItems(1);
    }
  }

  clearRmRelocationFilters(): void {
    this.rmRelocationLotNumberFilter.set('');
    this.rmRelocationItemCodeFilter.set('');
    this.rmRelocationManufacturerFilter.set('');
    this.rmRelocationProviderFilter.set('');
    this.rmRelocationMfrLotFilter.set('');
    this.searchAvailableLotItemsForRelocation(1);
  }

  onLotItemFilterChange(): void {
    this.debouncedSearchLotItemsFiltered();
  }

  onLotItemManufacturerSearch(event: AutoCompleteCompleteEvent): void {
    this.businessProxyService.search({ name: event.query, businessTypes: [BusinessEntityType.Manufacturer] }, 1, 15).subscribe({
      next: (response) => this.lotItemManufacturerSuggestions.set(response.data?.items ?? []),
      error: () => this.lotItemManufacturerSuggestions.set([]),
    });
  }

  onLotItemManufacturerChange(manufacturer: Business | null): void {
    this.lotItemManufacturerFilter.set(manufacturer);
    this.searchLotItems(1);
  }

  onRmRelocationFilterChange(): void {
    this.debouncedSearchRmRelocationItems();
  }

  searchAvailableLotItemsForRelocation(page = 1): void {
    const transfer = this.originalTransfer();
    const locationPrefix = this.sourceLocationCode() ?? transfer?.fromLocationCode ?? ImportantLocations.RAW_MATERIAL;
    const searchTerm = this.rmRelocationLotNumberFilter().trim() || undefined;
    const itemCode = this.rmRelocationItemCodeFilter().trim() || undefined;
    const manufacturerName = this.rmRelocationManufacturerFilter().trim() || undefined;
    const providerName = this.rmRelocationProviderFilter().trim() || undefined;
    const manufacturerLotNumber = this.rmRelocationMfrLotFilter().trim() || undefined;

    this.increaseLoading();
    this.lotItemTransferProxyService
      .getAvailableLotItems(
        locationPrefix,
        page,
        this.pageSize(),
        searchTerm,
        itemCode,
        manufacturerName,
        providerName,
        manufacturerLotNumber,
      )
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const items = response.data.items;
            this.rmRelocationTotalRecords.set(response.data.totalCount);
            this.rmRelocationCurrentPage.set(page);

            const allBusinessIds = [
              ...new Set([
                ...items.map((i) => i.manufacturerReference).filter((id): id is string => !!id),
                ...items.map((i) => i.providerReference).filter((id): id is string => !!id),
              ]),
            ];
            // Show results immediately without waiting for business name lookup
            this.rmRelocationResults.set(items);

            if (allBusinessIds.length === 0) return;

            this.businessProxyService.getByIds(allBusinessIds).subscribe({
              next: (bizResponse) => {
                const nameMap = new Map<string, string>();
                if (bizResponse.success && bizResponse.data) {
                  bizResponse.data.forEach((b) => {
                    if (b.id) nameMap.set(b.id, b.name);
                  });
                }
                this.rmRelocationResults.update((current) =>
                  current.map((item) => ({
                    ...item,
                    manufacturerName: item.manufacturerReference
                      ? (nameMap.get(item.manufacturerReference) ?? item.manufacturerName)
                      : item.manufacturerName,
                    providerName: item.providerReference ? (nameMap.get(item.providerReference) ?? item.providerName) : item.providerName,
                  })),
                );
              },
            });
          }
        },
        error: () => {
          this.rmRelocationResults.set([]);
          this.rmRelocationTotalRecords.set(0);
        },
      });
  }

  onRmRelocationPageChange(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize();
    const page = Math.floor(first / rows) + 1;
    this.pageSize.set(rows);
    this.searchAvailableLotItemsForRelocation(page);
  }

  addAvailableLotItemForRelocation(item: AvailableLotItemDto): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    if (!this.targetLocationCode()) {
      GlobalMessageUtil.error(
        this.translate('Error', 'Error'),
        this.translate('Target_Location_Required', 'Please select a target location before adding items'),
      );
      return;
    }

    const command: BatchAddLotItemKeyCommand = {
      items: [{ number: item.number, entityDefinitionCode: GlobalEntityDefinition.LotItem }],
    };

    this.increaseLoading();
    this.lotItemTransferProxyService
      .batchAddItems(transfer.id, command)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.rmRelocationResults.update((current) => current.filter((r) => r.number !== item.number));
            this.rmRelocationTotalRecords.update((n) => Math.max(0, n - 1));
            this.loadLotItemDetailsForAddedItems();
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Lot_Item_Added_Success', 'Lot item {{number}} added', { number: item.number }),
            );
          }
        },
      });
  }

  removeLotItemForRelocation(lotItem: LotItemSearchResultItem): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    this.increaseLoading();
    this.lotItemTransferProxyService
      .removeItem(transfer.id, lotItem.number!, GlobalEntityDefinition.LotItem)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            this.addedLotItems.update((current) => current.filter((li) => li.number !== lotItem.number!));
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Lot_Item_Removed_Success', 'Lot item {{number}} removed', { number: lotItem.number! }),
            );
            this.searchAvailableLotItemsForRelocation(this.rmRelocationCurrentPage());
          }
        },
      });
  }

  updateStatus(newStatus: LotItemTransferStatus): void {
    const transfer = this.originalTransfer();
    if (!transfer || newStatus === transfer.status) return;

    const command: LotItemTransferStatusCommand = { newStatus };

    this.increaseLoading();
    this.lotItemTransferProxyService
      .updateStatus(transfer.id, command)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Success', 'Success'),
            this.translate('Transfer_Status_Updated', 'Transfer {{number}} status updated', { number: transfer.number }),
          );
          this.loadTransfer(transfer.id);
        },
        error: () => {
          this.status.setValue(transfer.status, { emitEvent: false });
        },
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onNoteAdded(_note: Note): void {
    const transfer = this.originalTransfer();
    if (!transfer) return;

    // The csl-standard-notes component already adds the note to the notes array internally.
    // Just use the current notes as-is to avoid duplication.
    const currentNotes = transfer.notes || [];

    this.increaseLoading();
    this.lotItemTransferProxyService
      .updateNotes(transfer.id, currentNotes)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.originalTransfer.set(response.data);
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Note_Added_Success', 'Note added successfully'),
            );
          }
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/lot-item-transfers']);
  }

  getStatusLabel(status: LotItemTransferStatus): string {
    return this.statuses[status] || status.toString();
  }

  getRollFromSearchResults(rollNumber: string): Roll | undefined {
    return this.searchResults().find((r) => r.number === rollNumber);
  }

  getRollWeight(roll: Roll): number {
    return this.useStationWeight() ? roll.lastRecordedWeightLbs || 0 : roll.finalWeightLbs || 0;
  }

  lbsToKg(lbs: number): number {
    return lbs * 0.453592;
  }

  getQualityLabel(quality: number | undefined): string {
    if (quality === undefined) return '-';
    return quality === RollQuality.First ? this.translate('Quality_First', 'First') : this.translate('Quality_B_Grade', 'B-Grade');
  }

  getQualitySeverity(quality: number | undefined): 'success' | 'warn' | 'secondary' {
    if (quality === undefined) return 'secondary';
    return quality === RollQuality.First ? 'success' : 'warn';
  }

  getQualityIcon(quality: number | undefined): string {
    if (quality === undefined) return 'pi pi-question';
    return quality === RollQuality.First ? 'pi pi-star-fill' : 'pi pi-tag';
  }

  getCategoryLabel(category: number | undefined): string {
    if (category === undefined) return '-';
    switch (category) {
      case RollCategory.A:
        return this.translate('Category_A', 'A');
      case RollCategory.B:
        return this.translate('Category_B', 'B');
      case RollCategory.C:
        return this.translate('Category_C', 'C');
      default:
        return '-';
    }
  }

  getCategorySeverity(category: number | undefined): 'success' | 'warn' | 'danger' | 'secondary' {
    if (category === undefined) return 'secondary';
    switch (category) {
      case RollCategory.A:
        return 'success';
      case RollCategory.B:
        return 'warn';
      case RollCategory.C:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  // ==================== Location Tree Select Methods ====================

  private convertLocationsToTreeNodes(locations: LocationNode[], storableOnly: boolean): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    const locationMap = new Map<string, TreeNode>();

    locations.forEach((loc) => {
      const node: TreeNode = {
        key: loc.locationCode,
        label: loc.code + ' - ' + (loc.name?.en || loc.code),
        data: loc,
        leaf: true,
        selectable: storableOnly ? loc.isStorable : true,
      };
      locationMap.set(loc.locationCode, node);
    });

    locations.forEach((loc) => {
      const node = locationMap.get(loc.locationCode);
      if (node) {
        if (loc.parentCode) {
          const parentNode = locationMap.get(loc.parentCode);
          if (parentNode) {
            if (!parentNode.children) {
              parentNode.children = [];
              parentNode.leaf = false;
            }
            parentNode.children.push(node);
          } else {
            treeNodes.push(node);
          }
        } else {
          treeNodes.push(node);
        }
      }
    });

    return treeNodes;
  }

  getTransferTypeLabel(type: LotItemTransferType): string {
    return this.transferTypes[type] || type.toString();
  }

  getTransferTypeSeverity(type: LotItemTransferType): 'info' | 'warn' {
    return type === LotItemTransferType.Relocation ? 'info' : 'warn';
  }

  getTransferTypeIcon(type: LotItemTransferType): string {
    return type === LotItemTransferType.Relocation ? 'pi pi-arrows-alt' : 'pi pi-arrow-right';
  }
}
